// CreateActionModal — single shared modal for creating and editing actions.
//
// Entry points:
//   1. Actions page → "New Action" button → opens blank (create mode)
//   2. AI Recommendations page → "Create Action from Insight" → opens pre-filled (create mode)
//   3. Actions page → click card in "Assigned by Me" tab → opens pre-populated (edit mode)
//
// Charlotte / Devin wiring notes:
//   • The onConfirm callback receives a CreateActionPayload — pass to POST /api/actions.
//   • The onUpdate callback receives an UpdateActionPayload — pass to PUT /api/actions/:id.
//   • If reassignment occurs (original assignee differs from updated assignee), the backend
//     should archive the original action and create a new one — matching the frontend demo behaviour.
//   • The AssigneePicker stores pins in localStorage keyed by persona ID.
//     If you want server-side pin persistence, swap the localStorage calls in
//     AssigneePicker.tsx usePinnedUsers() for API calls — the interface is the same.

import { AlertCircle, Pencil, Plus, PlusCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/contexts/AppContext";
import { useUsers } from "@/contexts/UsersContext";
import type {
  Action,
  CreateActionPayload,
  UpdateActionPayload,
} from "@/types/actions";
import { AssigneePicker } from "./AssigneePicker";

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CreateActionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: CreateActionPayload) => void;
  onUpdate?: (payload: UpdateActionPayload) => void;

  /**
   * When provided, the modal opens in edit mode pre-populated with the action's data.
   * onUpdate must also be provided when using editAction.
   */
  editAction?: Action;

  /**
   * Optional pre-population for create mode. Pass title + description when opening from an
   * insight or drill-down context. All other fields (assignee, due date) are
   * always left for the user to choose deliberately.
   */
  prefill?: {
    title?: string;
    description?: string;
    assignedTo?: string;
    relatedInvoiceId?: string;
    relatedInvoiceRef?: string;
    relatedCustomer?: string;
  };

  /**
   * Controls the header icon and title wording.
   * 'action'  → "+ Create Action"                    (from Actions page)
   * 'insight' → "⊕ Create Action from Insight"       (from Recommendations page)
   */
  variant?: "action" | "insight";
}

// ── Component ─────────────────────────────────────────────────────────────────

export const CreateActionModal: React.FC<CreateActionModalProps> = ({
  open,
  onClose,
  onConfirm,
  onUpdate,
  editAction,
  prefill,
  variant = "action",
}) => {
  const { currentUser } = useApp();
  const { users } = useUsers();

  const isEditMode = !!editAction;

  const defaultDueDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + (variant === "insight" ? 7 : 1));
    return d;
  };

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState<Date | undefined>(defaultDueDate);

  // Track original assignee in edit mode to detect reassignment
  const [originalAssignee, setOriginalAssignee] = useState("");

  // Reset / populate fields when modal opens
  useEffect(() => {
    if (open) {
      if (isEditMode && editAction) {
        setTitle(editAction.title);
        setDescription(editAction.description ?? "");
        setAssignedTo(editAction.assignedTo);
        setOriginalAssignee(editAction.assignedTo);
        setDueDate(editAction.dueDate);
      } else {
        setTitle(prefill?.title ?? "");
        setDescription(prefill?.description ?? "");
        setAssignedTo(prefill?.assignedTo ?? "");
        setOriginalAssignee("");
        setDueDate(defaultDueDate());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEditMode]);

  const isReassigned =
    isEditMode && assignedTo !== originalAssignee && assignedTo !== "";

  const getNewAssigneeName = () => {
    const user = users.find((u) => u.id === assignedTo);
    return user?.name ?? "the new assignee";
  };

  const isValid = title.trim() && assignedTo && dueDate;

  const handleSubmit = () => {
    if (!isValid || !dueDate) return;
    if (isEditMode && editAction && onUpdate) {
      onUpdate({
        actionId: editAction.id,
        title: title.trim(),
        description: description.trim() || undefined,
        assignedTo,
        dueDate,
      });
    } else {
      onConfirm({
        title: title.trim(),
        description: description.trim() || undefined,
        assignedTo,
        dueDate,
        relatedInvoiceId: prefill?.relatedInvoiceId,
        relatedInvoiceRef: prefill?.relatedInvoiceRef,
        relatedCustomer: prefill?.relatedCustomer,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey && isValid) handleSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <Pencil className="h-5 w-5 text-primary" />
                Edit Action
              </>
            ) : variant === "insight" ? (
              <>
                <PlusCircle className="h-5 w-5 text-primary" />
                Create Action from Insight
              </>
            ) : (
              <>
                <Plus className="h-5 w-5" />
                Create Action
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Invoice / insight context banner */}
          {!isEditMode && prefill?.relatedInvoiceRef && (
            <div className="flex items-center gap-2 text-xs bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2">
              <span className="font-mono font-medium">
                {prefill.relatedInvoiceRef}
              </span>
              {prefill.relatedCustomer && (
                <span>— {prefill.relatedCustomer}</span>
              )}
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="action-title">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="action-title"
              placeholder={
                isEditMode
                  ? "Action title…"
                  : variant === "insight"
                    ? "Action title…"
                    : "e.g. Chase overdue invoice from Mersey Build Group"
              }
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="action-description">
              {variant === "insight" ? "Description" : "Additional notes"}
            </Label>
            <Textarea
              id="action-description"
              placeholder={
                variant === "insight"
                  ? "Context and detail for the assignee…"
                  : "Any context the assignee might need…"
              }
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={variant === "insight" ? 4 : 2}
              className="resize-none"
            />
          </div>

          {/* Assign to + Due date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Assign to <span className="text-destructive">*</span>
              </Label>
              <AssigneePicker
                users={users}
                value={assignedTo}
                onChange={setAssignedTo}
                personaId={currentUser.id}
                includeSelf
                currentUserId={currentUser.id}
              />
            </div>

            <div className="space-y-2">
              <Label>
                Due date <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                placeholder="Select date"
                disabledDate={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
              />
            </div>
          </div>

          {/* Reassignment warning — full width, below the grid */}
          {isReassigned && (
            <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Changing the assignee will archive this action record and
                generate a new action record with the same details assigned to{" "}
                <strong>{getNewAssigneeName()}</strong>.
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid}>
            {isEditMode ? (
              <>
                <Pencil className="h-4 w-4 mr-2" />
                Save Changes
              </>
            ) : variant === "insight" ? (
              <>
                <PlusCircle className="h-4 w-4 mr-2" />
                Create Action
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Create Action
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
